import { useEffect, useState, useCallback } from 'react'
import { fetchActiveSubscription, type Subscription } from '../lib/paystack'
import { useAuth } from '../lib/auth'

interface SubscriptionState {
    subscription: Subscription | null
    isActive: boolean
    loading: boolean
    error: string | null
    refresh: () => void
}

interface Options {
    /** Set true to completely skip the Supabase query (e.g. during active trial) */
    skip?: boolean
}

export function useSubscription({ skip = false }: Options = {}): SubscriptionState {
    const { user } = useAuth()
    const [subscription, setSubscription] = useState<Subscription | null>(() => {
        const cached = localStorage.getItem('moniflow_subscription');
        return cached ? JSON.parse(cached) : null;
    })

    // If we have cached sub, don't show loading
    const [loading, setLoading] = useState(!skip && !localStorage.getItem('moniflow_subscription'))
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(async () => {
        if (!user || skip) {
            setLoading(false)
            return
        }

        // Background fetch if we have cache
        if (!subscription) setLoading(true)

        const { data, error } = await fetchActiveSubscription()
        if (error) {
            if (!subscription) setError(error)
        } else {
            setSubscription(data)
            localStorage.setItem('moniflow_subscription', JSON.stringify(data));
        }
        setLoading(false)
    }, [user, skip, subscription])

    useEffect(() => {
        load()
    }, [load])

    const isActive = subscription !== null &&
        subscription.status === 'active' &&
        new Date(subscription.expires_at) > new Date()

    return { subscription, isActive, loading, error, refresh: load }
}
